// services/adminMemoirService.js
import axios from 'axios';

const API_URL = process.env.REACT_APP_API_URL;

// Create a base axios instance for admin memoir operations
const adminMemoirApi = axios.create({
  baseURL: `${API_URL}/api/v1/admin/memoir`,
});

// Request interceptor to add token
adminMemoirApi.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem('adminToken');
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }

    // For FormData, let the browser set the Content-Type
    if (!(config.data instanceof FormData)) {
      config.headers['Content-Type'] = 'application/json';
    }

    return config;
  },
  (error) => Promise.reject(error)
);

// Response interceptor to handle errors
adminMemoirApi.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response?.status === 401) {
      localStorage.removeItem('adminToken');
      localStorage.removeItem('adminUser');
      window.location.href = '/admin/login';
    }
    return Promise.reject(error);
  }
);

const adminMemoirService = {
  // ==================== CATEGORY MANAGEMENT ====================

  /**
   * Get all categories
   */
  getCategories: async (params = {}) => {
    console.log('📤 [AdminMemoirService] Fetching categories...');
    try {
      const response = await adminMemoirApi.get('/categories', { params });
      console.log('📥 [AdminMemoirService] Categories fetched:', response.data);
      return response.data;
    } catch (error) {
      console.error('💥 [AdminMemoirService] Error fetching categories:', error);
      throw error;
    }
  },

  /**
   * Get category by ID with its questions
   */
  getCategoryById: async (categoryId) => {
    console.log('📤 [AdminMemoirService] Fetching category:', categoryId);
    try {
      const response = await adminMemoirApi.get(`/categories/${categoryId}`);
      console.log('📥 [AdminMemoirService] Category fetched:', response.data);
      return response.data;
    } catch (error) {
      console.error('💥 [AdminMemoirService] Error fetching category:', error);
      throw error;
    }
  },

  /**
   * Create new category with cover image
   */
  createCategory: async (categoryData) => {
    console.log('📤 [AdminMemoirService] Creating category...');
    try {
      const formData = new FormData();
      formData.append('name', categoryData.name);
      formData.append('description', categoryData.description);
      if (categoryData.coverImage) {
        formData.append('coverImage', categoryData.coverImage);
      }

      const response = await adminMemoirApi.post('/categories', formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      });
      console.log('📥 [AdminMemoirService] Category created:', response.data);
      return response.data;
    } catch (error) {
      console.error('💥 [AdminMemoirService] Error creating category:', error);
      throw error;
    }
  },

  /**
   * Update category
   */
  updateCategory: async (categoryId, categoryData) => {
    console.log('📤 [AdminMemoirService] Updating category:', categoryId);
    try {
      const formData = new FormData();
      if (categoryData.name) formData.append('name', categoryData.name);
      if (categoryData.description) formData.append('description', categoryData.description);
      if (categoryData.coverImage) formData.append('coverImage', categoryData.coverImage);

      const response = await adminMemoirApi.put(`/categories/${categoryId}`, formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      });
      console.log('📥 [AdminMemoirService] Category updated:', response.data);
      return response.data;
    } catch (error) {
      console.error('💥 [AdminMemoirService] Error updating category:', error);
      throw error;
    }
  },

  /**
   * Delete category (soft delete)
   */
  deleteCategory: async (categoryId) => {
    console.log('📤 [AdminMemoirService] Deleting category:', categoryId);
    try {
      const response = await adminMemoirApi.delete(`/categories/${categoryId}`);
      console.log('📥 [AdminMemoirService] Category deleted:', response.data);
      return response.data;
    } catch (error) {
      console.error('💥 [AdminMemoirService] Error deleting category:', error);
      throw error;
    }
  },

  // ==================== QUESTION MANAGEMENT ====================

  /**
   * Get questions by category
   */
  getQuestionsByCategory: async (categoryId) => {
    console.log('📤 [AdminMemoirService] Fetching questions for category:', categoryId);
    try {
      const response = await adminMemoirApi.get(`/categories/${categoryId}/questions`);
      console.log('📥 [AdminMemoirService] Questions fetched:', response.data);
      return response.data;
    } catch (error) {
      console.error('💥 [AdminMemoirService] Error fetching questions:', error);
      throw error;
    }
  },

  /**
   * Create questions for a category
   */
  createQuestions: async (questionsData) => {
    console.log('📤 [AdminMemoirService] Creating questions...');
    try {
      const response = await adminMemoirApi.post('/questions', questionsData);
      console.log('📥 [AdminMemoirService] Questions created:', response.data);
      return response.data;
    } catch (error) {
      console.error('💥 [AdminMemoirService] Error creating questions:', error);
      throw error;
    }
  },

  /**
   * Update question
   */
  updateQuestion: async (questionId, questionData) => {
    console.log('📤 [AdminMemoirService] Updating question:', questionId);
    try {
      const response = await adminMemoirApi.put(`/questions/${questionId}`, questionData);
      console.log('📥 [AdminMemoirService] Question updated:', response.data);
      return response.data;
    } catch (error) {
      console.error('💥 [AdminMemoirService] Error updating question:', error);
      throw error;
    }
  },

  /**
   * Delete question
   */
  deleteQuestion: async (questionId) => {
    console.log('📤 [AdminMemoirService] Deleting question:', questionId);
    try {
      const response = await adminMemoirApi.delete(`/questions/${questionId}`);
      console.log('📥 [AdminMemoirService] Question deleted:', response.data);
      return response.data;
    } catch (error) {
      console.error('💥 [AdminMemoirService] Error deleting question:', error);
      throw error;
    }
  },

  /**
   * Bulk update questions (reorder, etc.)
   */
  bulkUpdateQuestions: async (categoryId, questions) => {
    console.log('📤 [AdminMemoirService] Bulk updating questions...');
    try {
      const response = await adminMemoirApi.put(`/categories/${categoryId}/questions/bulk`, { questions });
      console.log('📥 [AdminMemoirService] Questions bulk updated:', response.data);
      return response.data;
    } catch (error) {
      console.error('💥 [AdminMemoirService] Error bulk updating questions:', error);
      throw error;
    }
  },

  // ==================== MEMOIR ORDER MANAGEMENT ====================

  /**
   * List all memoir orders (with filters, search, pagination)
   *
   * @param {Object} params
   * @param {number} [params.page=1]
   * @param {number} [params.limit=10]
   * @param {string} [params.status]        - 'active' | 'completed' | 'pending' | 'processing' | 'cancelled'
   * @param {string} [params.paymentStatus] - 'pending' | 'processing' | 'paid' | 'failed' | 'refunded'
   * @param {string} [params.category]      - Mongo ObjectId
   * @param {string} [params.search]        - matches orderNumber, title, customer name, email
   * @param {string} [params.dateFrom]      - ISO string
   * @param {string} [params.dateTo]        - ISO string
   * @param {string} [params.sort]          - e.g. '-createdAt'
   *
   * Response shape:
   *   {
   *     success: true,
   *     data: {
   *       memoirs: [ { _id, orderNumber, customer, title, type, delivery,
   *                    progress, totalWeeks, status, statusRaw, statusConfig, ... } ],
   *       pagination: { page, limit, total, pages },
   *       stats: { total, active, completed, inProgress }   // optional
   *     }
   *   }
   */
  getMemoirOrders: async (params = {}) => {
    console.log('📤 [AdminMemoirService] Fetching memoir orders...', params);
    try {
      const response = await adminMemoirApi.get('/orders', { params });
      console.log('📥 [AdminMemoirService] Memoir orders fetched:', response.data);
      return response.data;
    } catch (error) {
      console.error('💥 [AdminMemoirService] Error fetching memoir orders:', error);
      throw error;
    }
  },

  /**
   * Get a single memoir order by ID (fully populated for the detail modal)
   */
  getMemoirOrderById: async (orderId) => {
    console.log('📤 [AdminMemoirService] Fetching memoir order:', orderId);
    try {
      const response = await adminMemoirApi.get(`/orders/${orderId}`);
      console.log('📥 [AdminMemoirService] Memoir order fetched:', response.data);
      return response.data;
    } catch (error) {
      console.error('💥 [AdminMemoirService] Error fetching memoir order:', error);
      throw error;
    }
  },

  /**
   * Update a memoir order's status / admin notes / progress.
   *
   * @param {string} orderId
   * @param {Object} payload
   * @param {string} [payload.status]          - 'pending' | 'processing' | 'active' | 'completed' | 'cancelled'
   * @param {string} [payload.paymentStatus]   - 'pending' | 'processing' | 'paid' | 'failed' | 'refunded'
   * @param {string} [payload.adminNotes]
   * @param {number} [payload.currentWeek]
   * @param {number} [payload.questionsSent]
   * @param {number} [payload.storiesUploaded]
   */
  updateMemoirOrderStatus: async (orderId, payload) => {
    console.log('📤 [AdminMemoirService] Updating memoir order:', orderId, payload);
    try {
      const response = await adminMemoirApi.patch(`/orders/${orderId}/status`, payload);
      console.log('📥 [AdminMemoirService] Memoir order updated:', response.data);
      return response.data;
    } catch (error) {
      console.error('💥 [AdminMemoirService] Error updating memoir order:', error);
      throw error;
    }
  },

  // ==================== STATISTICS ====================

  /**
   * Get memoir statistics
   */
  getMemoirStats: async () => {
    console.log('📤 [AdminMemoirService] Fetching memoir statistics...');
    try {
      const response = await adminMemoirApi.get('/stats');
      console.log('📥 [AdminMemoirService] Statistics fetched:', response.data);
      return response.data;
    } catch (error) {
      console.error('💥 [AdminMemoirService] Error fetching statistics:', error);
      throw error;
    }
  },

  // ==================== UTILITIES ====================

  /**
   * Upload image to Cloudinary
   */
  uploadImageToCloudinary: async (file, uploadPreset = null) => {
    const formData = new FormData();
    formData.append('file', file);
    formData.append('upload_preset', uploadPreset || process.env.REACT_APP_CLOUDINARY_UPLOAD_PRESET);

    try {
      const response = await axios.post(
        `https://api.cloudinary.com/v1_1/${process.env.REACT_APP_CLOUDINARY_CLOUD_NAME}/image/upload`,
        formData,
        {
          headers: {
            'Content-Type': 'multipart/form-data'
          },
          timeout: 30000
        }
      );

      return {
        url: response.data.secure_url,
        publicId: response.data.public_id
      };
    } catch (error) {
      console.error('💥 [AdminMemoirService] Error uploading to Cloudinary:', error);
      throw error;
    }
  },

  /**
   * Format date
   */
  formatDate: (date) => {
    if (!date) return '';
    return new Date(date).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  },

  /**
   * Format date with time
   */
  formatDateTime: (date) => {
    if (!date) return '';
    return new Date(date).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  },

  /**
   * Format price in Naira
   */
  formatNaira: (amount) => {
    const n = Number(amount) || 0;
    if (n >= 1_000_000) return `₦${(n / 1_000_000).toFixed(1)}M`;
    if (n >= 1_000) return `₦${(n / 1_000).toFixed(1)}K`;
    return `₦${n.toLocaleString()}`;
  },

  /**
   * Create slug from name
   */
  createSlug: (name) => {
    return name
      .toLowerCase()
      .replace(/[^a-z0-9]+/g, '-')
      .replace(/(^-|-$)/g, '');
  }
};

export default adminMemoirService;